import {
  Box,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  UseDisclosureReturn,
  VStack,
  useDisclosure
} from "@chakra-ui/react"
import useKeyControl from "@pathwright/utils/src/keyboard/useKeyControl"
import { Command, useCommandState } from "cmdk"
import { ComponentProps, ReactNode, useEffect, useState } from "react"
import PWAdminCmdKAuth from "./PWAdminCmdKAuth"
import { usePWAdminCmdKContext } from "./PWAdminCmdKContext"
import PWAdminCmdKOperations from "./PWAdminCmdKGraphQLOperations"
import PWAdminCmdKItem from "./PWAdminCmdKItem"
import PWAdminCmdKItems from "./PWAdminCmdKItems"
import { PWAdminCmdKOption } from "./types"
import useIsPWAdminUtilEnabled from "./useIsPWAdminUtilEnabled"
import { usePWAdminSSOOptions } from "./usePWAdminSSOLink"
import useSupportInfo from "./useSupportInfo"

function SearchStateHook({
  onSearchChange
}: {
  onSearchChange: (search: string) => void
}) {
  const search = useCommandState((state) => state.search)
  // This hook is used to trigger re-renders when the search value changes.
  useEffect(() => {
    onSearchChange(search)
  }, [search])

  return null
}

export const PWAdminCmdKMenu = ({
  children,
  ...cmdProps
}: {
  children: ReactNode | ReactNode[]
} & ComponentProps<typeof Command>) => {
  const [search, setSearch] = useState("")

  return (
    <Command
      className="PWAdminCmdKMenu"
      label="Command Menu"
      // Only filter when searching. This allows to choose only render certain items
      // when searching, yet those items will still be indexed for search. Whereas,
      // if these items weren't rendered initially, they wouldn't be indexed (and thus,
      // unsearchable).
      shouldFilter={!!search}
      loop
      {...cmdProps}
    >
      <SearchStateHook onSearchChange={setSearch} />
      <VStack
        w="full"
        alignItems="stretch"
        p={4}
        bg="black"
        borderRadius="card"
      >
        <Input
          as={Command.Input}
          placeholder="Search..."
          color="whiteAlpha.500"
          borderColor="whiteAlpha.200"
          _focus={{ bgColor: "whiteAlpha.200" }}
          _placeholder={{ color: "whiteAlpha.500" }}
        />
        <Command.List>
          <Box as={Command.Empty} px={2} py={1} color="whiteAlpha.500">
            No results found.
          </Box>
          {children}
        </Command.List>
      </VStack>
    </Command>
  )
}

export const PWAdminCmdKModal = ({ isOpen, onClose }: UseDisclosureReturn) => {
  const supportInfo = useSupportInfo()
  const pwAdminSSOOptions = usePWAdminSSOOptions()
  const { hintsActive, setHintsActive } = usePWAdminCmdKContext()

  const hintOption: PWAdminCmdKOption = {
    label: "Hints",
    icon: "lightbulb",
    active: hintsActive,
    onSelect: () => setHintsActive(!hintsActive)
  }

  const [pages, setPages] = useState<"operations"[]>([])
  const page = pages[pages.length - 1]

  // NOTE: always rendering Modal to ensure any keyboard listeners are enabled regardless if
  // modal is open. We could loop through each option and setup the listeners that way,
  // but that's less intuitive. Ideally, the Modal could optionally render its content regardless
  // if open via some config, but it doesn't appear to support that.
  return (
    <Modal
      isOpen
      onClose={onClose}
      trapFocus={isOpen}
      blockScrollOnMount={isOpen}
      closeOnEsc={!page}
    >
      {isOpen && <ModalOverlay />}
      <ModalContent
        bg="transparent"
        borderRadius="card"
        sx={{
          // Visibly hide parent of modal content when not open, but keep mounted.
          ".chakra-modal__content-container:has(> &)": {
            visibility: isOpen ? "visible" : "hidden"
          }
        }}
      >
        <ModalBody p={0}>
          <PWAdminCmdKMenu
            onKeyDown={(e) => {
              // Escape goes to previous page
              // Backspace goes to previous page when search is empty
              if (
                page &&
                e.key === "Escape"
                // || (e.key === 'Backspace' && !search)
              ) {
                setPages((pages) => pages.slice(0, -1))
              }
            }}
          >
            {!page && (
              <>
                <PWAdminCmdKItem option={supportInfo} />
                <PWAdminCmdKItems options={pwAdminSSOOptions} />
                <PWAdminCmdKAuth />
                <PWAdminCmdKItem option={hintOption} />
                <PWAdminCmdKItem
                  option={{
                    label: "GraphQL Operations",
                    icon: "nested-list",
                    onSelect: () =>
                      setPages((pages) => [...pages, "operations"])
                  }}
                />
              </>
            )}

            {page === "operations" && <PWAdminCmdKOperations />}
          </PWAdminCmdKMenu>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const ConditionallyEnabledPWAdminCmdK = () => {
  const disclosure = useDisclosure()
  const { isOpen, onToggle } = disclosure
  const { checkPWAdminEnabled } = usePWAdminCmdKContext()
  const isPWAdminUtilEnabled = useIsPWAdminUtilEnabled()

  // Toggle the menu via keyboard control.
  // NOTE: the path navigator uses the Meta+k keyboard shortcut,
  // hence the Shift.
  // Including other keyboard shortcuts that may conflict less with other
  // apps and such (Notion desktop app globally intercepts Meta+Shift+K).
  useKeyControl(["Meta+Shift+K", "Control+Shift+K"], onToggle)

  useEffect(() => {
    if (isOpen && !isPWAdminUtilEnabled) {
      // Attempt to check if enabled, which may fail.
      // We're getting flaky results from this check, where sometimes it may work
      // and other times it may not, due to not properly being able to access local
      // storeage across domains.
      checkPWAdminEnabled()
    }
  }, [isOpen, isPWAdminUtilEnabled])

  return isPWAdminUtilEnabled ? <PWAdminCmdKModal {...disclosure} /> : null
}

export default ConditionallyEnabledPWAdminCmdK
